/**
 * Packaging Table Component - Display detailed packaging data table
 */

import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Package, Calendar } from 'lucide-react';
import { usePackagingTableData } from '@/hooks/useProductionData';
import { ProductionFilters } from '@/types/production';

interface PackagingTableProps {
  filters: ProductionFilters;
}

export const PackagingTable: React.FC<PackagingTableProps> = ({ filters }) => {
  // Fetch packaging data (working days only for table)
  const {
    data: packagingData = [],
    isLoading,
    error
  } = usePackagingTableData(filters);

  // Format number with decimal places
  const formatNumber = (value: number | null, decimals: number = 2) => {
    if (value === null || value === undefined) return '-';
    return value.toFixed(decimals);
  };

  // Sort packaging data by date (newest first)
  const sortedPackagingData = React.useMemo(() => {
    return [...packagingData].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [packagingData]);

  // Debug: Log packaging data
  React.useEffect(() => {
    if (packagingData.length > 0) {
      console.log('📦 Packaging data for table:', packagingData.length, 'records');
      console.log('📦 Sample packaging record:', packagingData[0]);
    }
  }, [packagingData]);

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-red-600">Lỗi tải dữ liệu đóng gói</CardTitle>
          <CardDescription>{error.message}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5 text-green-600" />
          Dữ liệu thành phẩm đóng gói chi tiết
        </CardTitle>
        <CardDescription>
          Hiển thị đầy đủ các chỉ số đóng gói: M³ Wood, M³ Thùng, và các năng suất tương ứng
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : sortedPackagingData.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead rowSpan={2} className="vertical-align-bottom border-r bg-gray-50">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Ngày
                    </div>
                  </TableHead>
                  
                  {/* Công lao động */}
                  <TableHead className="text-center border-r bg-orange-50">Công lao động</TableHead>
                  
                  {/* M³ Wood */}
                  <TableHead className="text-center border-r bg-blue-50">M³ Wood</TableHead>
                  
                  {/* M³ Thùng */}
                  <TableHead className="text-center border-r bg-green-50">M³ Thùng</TableHead>
                  
                  {/* Năng suất Wood */}
                  <TableHead className="text-center border-r bg-purple-50">NS Wood</TableHead>
                  
                  {/* Năng suất Thùng */}
                  <TableHead className="text-center border-r bg-indigo-50">NS Thùng</TableHead>
                  
                  {/* Số Container */}
                  <TableHead className="text-center bg-yellow-50">Số Container</TableHead>
                </TableRow>
                <TableRow>
                  {/* Công lao động */}
                  <TableHead className="text-xs border-r">Công</TableHead>
                  
                  {/* M³ Wood */}
                  <TableHead className="text-xs border-r">M³</TableHead>
                  
                  {/* M³ Thùng */}
                  <TableHead className="text-xs border-r">M³</TableHead>
                  
                  {/* Năng suất Wood */}
                  <TableHead className="text-xs border-r">M³/Công</TableHead>
                  
                  {/* Năng suất Thùng */}
                  <TableHead className="text-xs border-r">M³/Công</TableHead>
                  
                  {/* Số Container */}
                  <TableHead className="text-xs">Container</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedPackagingData.map((item, index) => (
                  <TableRow key={index} className="hover:bg-gray-50">
                    <TableCell className="font-medium border-r">
                      {new Date(item.date).toLocaleDateString('vi-VN')}
                    </TableCell>
                    
                    {/* Công lao động */}
                    <TableCell className="text-sm border-r text-center">
                      {formatNumber(item.labor, 1)}
                    </TableCell>
                    
                    {/* M³ Wood */}
                    <TableCell className="text-sm border-r text-center">
                      {formatNumber(item.volumeWood, 2)}
                    </TableCell>
                    
                    {/* M³ Thùng */}
                    <TableCell className="text-sm border-r text-center font-medium text-green-700">
                      {formatNumber(item.volumeCarton, 2)}
                    </TableCell>
                    
                    {/* Năng suất Wood */}
                    <TableCell className="text-sm border-r text-center">
                      {formatNumber(item.productivityWood, 3)}
                    </TableCell>
                    
                    {/* Năng suất Thùng */}
                    <TableCell className="text-sm border-r text-center font-medium text-indigo-700">
                      {formatNumber(item.productivityCarton, 3)}
                    </TableCell>
                    
                    {/* Số Container */}
                    <TableCell className="text-sm text-center font-medium text-yellow-700">
                      {formatNumber(item.containerCount, 1)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">Không có dữ liệu thành phẩm đóng gói</p>
            <p className="text-sm">Hãy thử điều chỉnh bộ lọc hoặc kiểm tra kết nối dữ liệu</p>
          </div>
        )}
        
        {/* Table Legend */}
        {sortedPackagingData.length > 0 && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium mb-3 text-gray-800">Chú thích bảng dữ liệu</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-orange-200 rounded"></div>
                  <span><strong>Công lao động:</strong> Số công nhân tham gia đóng gói</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-200 rounded"></div>
                  <span><strong>M³ Wood:</strong> Khối lượng gỗ được đóng gói (M³)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-200 rounded"></div>
                  <span><strong>M³ Thùng:</strong> Tổng khối lượng thùng carton (M³)</span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-purple-200 rounded"></div>
                  <span><strong>NS Wood:</strong> Năng suất đóng gói theo gỗ (M³/Công)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-indigo-200 rounded"></div>
                  <span><strong>NS Thùng:</strong> Năng suất đóng gói theo thùng (M³/Công)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-yellow-200 rounded"></div>
                  <span><strong>Số Container:</strong> Số lượng container đóng được/ngày</span>
                </div>
              </div>
            </div>
            
            {/* Note about charts */}
            <div className="mt-4 p-3 bg-yellow-50 rounded border-l-4 border-yellow-400">
              <p className="text-xs text-yellow-800">
                <strong>Ghi chú:</strong> Biểu đồ bên trên chỉ hiển thị dữ liệu liên quan đến <strong>M³ Thùng carton</strong> 
                và <strong>Năng suất theo thùng</strong> để tập trung vào chỉ số chính của thành phẩm đóng gói.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};